import { Box } from '@web/components/atoms/Layout'
import { Image as ChakraImage } from '@web/components/atoms/Image'
import type { Image as ImageProps } from '@web/graphql-components'
import { useMergeFields } from '../Text/MergeTags/MergeTag'
import { useMemo, useState } from 'react'
import { DESKTOP_CONTAINER_QUERY, MOBILE_CONTAINER_QUERY } from '../utils'

const Image = ({ alt, styles, mobileStyles, ...props }: ImageProps) => {
  const mergeFieldUrl = useMergeFields((state) => {
    if (!props.mergeFieldId) {
      return null
    }

    return state.mergeFields.get(props.mergeFieldId ?? '')
  })
  const url = useMemo(() => mergeFieldUrl || props.url, [props.url, mergeFieldUrl])
  const download = !!styles?.options?.download
  const fileUrl = styles?.href
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = async () => {
    if (!fileUrl || isDownloading) {
      return
    }

    setIsDownloading(true)

    try {
      const filename = alt || fileUrl.split('/').pop() || 'download'
      const response = await fetch(
        `/api/v1/download?url=${encodeURIComponent(fileUrl)}&filename=${encodeURIComponent(filename)}`,
      )
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = fileUrl.split('/').pop() || 'download'
      a.click()
      URL.revokeObjectURL(blobUrl)
    } finally {
      setIsDownloading(false)
    }
  }

  const clickableProps =
    download && fileUrl
      ? {
          as: 'button' as const,
          type: 'button',
          onClick: handleDownload,
          cursor: isDownloading ? 'wait' : 'pointer',
          bg: 'transparent',
          border: 'none',
          disabled: isDownloading,
        }
      : styles?.href
        ? { as: 'a' as const, href: styles.href, target: styles.target }
        : {}

  return url ? (
    <Box
      {...clickableProps}
      display="flex"
      justifyContent="center"
      flexGrow={1}
      className="c-component-wrapper"
      height="100%"
      w="100%"
      css={{
        [MOBILE_CONTAINER_QUERY]: {
          p: mobileStyles?.padding,
        },
        [DESKTOP_CONTAINER_QUERY]: {
          p: styles?.padding ?? '0',
        },
      }}
    >
      <ChakraImage
        src={url}
        alt={alt || ''}
        draggable="false"
        css={{
          objectFit: styles?.objectFit ?? 'contain',
          [MOBILE_CONTAINER_QUERY]: {
            width: mobileStyles?.imgWidth ?? '100%',
            objectPosition: mobileStyles?.objectPosition,
            height: mobileStyles?.height ?? '100%',
          },
          [DESKTOP_CONTAINER_QUERY]: {
            width: styles?.imgWidth ?? '100%',
            objectPosition: styles?.objectPosition,
            height: styles?.height ?? '100%',
          },
        }}
      />
    </Box>
  ) : null
}

export default Image
